import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { History, Share, Scale } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { weekLabel } from "@/lib/format";
import { exportConferencePdf } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { useLayout } from "@/context/LayoutContext";
import { ErrorState, Loading, Page, PageHeader } from "@/components/kit";
import { TargetAlert } from "@/pages/Dashboard";
import useConferenceEditor from "@/hooks/useConferenceEditor";
import ItemsPanel from "@/components/conference/ItemsPanel";
import { SummaryCard, SettingsCard, DeleteCard, ActionBar } from "@/components/conference/EditorParts";
import InvoicesSection from "@/components/conference/InvoicesSection";
import LossesSection from "@/components/conference/LossesSection";
import { ConferenceTour } from "@/components/conference/ConferenceTour";

function HeaderActions({ conf }) {
  const nav = useNavigate();
  const { isMgmt } = useAuth();
  const { unit } = useUnit();
  const { layout } = useLayout();
  const exportPdf = () => !exportConferencePdf(conf, unit?.name, layout?.app_name) && toast.error("Não foi possível gerar o PDF");
  return (
    <div className="flex gap-2">
      {isMgmt && <button className="icon-btn" onClick={() => nav(`/cmv-analysis?conf=${conf.id}`)} title="CMV teórico x real" data-testid="conference-theoretical-button"><Scale size={17} /></button>}
      <button className="icon-btn" onClick={() => nav(`/conference-history/${conf.id}`)} title="Histórico" data-testid="conference-history-button"><History size={17} /></button>
      <button className="icon-btn" onClick={exportPdf} title="Exportar PDF" data-testid="export-pdf-button"><Share size={17} /></button>
      <ConferenceTour editable={conf.status === "aberta"} />
    </div>
  );
}

function Editor({ conf }) {
  const { isMgmt, user } = useAuth();
  const { unit } = useUnit();
  const ed = useConferenceEditor(conf);
  const [filter, setFilter] = useState("all");
  const open = conf.status === "aberta";
  const restricted = !isMgmt && (user.sector_ids || []).length > 0;
  const mine = { restricted, items: restricted ? conf.items.filter((i) => i.editable) : conf.items };
  return (
    <Page>
      <PageHeader title={weekLabel(conf)} back="/conferences" subtitle={unit?.name} right={<HeaderActions conf={conf} />} />
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          <SummaryCard conf={conf} ed={ed} isMgmt={isMgmt} mine={mine} />
          {isMgmt && <TargetAlert value={conf.summary.pct} target={conf.cmv_target} testid="conference-target-alert" />}
          <ItemsPanel conf={conf} ed={ed} isMgmt={isMgmt} filter={filter} setFilter={setFilter} />
        </div>
        <div className="flex flex-col gap-4 lg:sticky lg:top-6">
          {isMgmt && <SettingsCard conf={conf} ed={ed} />}
          {isMgmt && <InvoicesSection conf={conf} editable={open} invalidate={ed.invalidate} />}
          <LossesSection conf={conf} editable={open} invalidate={ed.invalidate} />
          {isMgmt && <DeleteCard ed={ed} />}
        </div>
      </div>
      <ActionBar conf={conf} ed={ed} isMgmt={isMgmt} onViewPending={() => setFilter("pending")} />
    </Page>
  );
}

export default function ConferenceEditor() {
  const { id } = useParams();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["conference", id], queryFn: () => api(`/conferences/${id}`) });
  if (isLoading) return <Loading />;
  if (isError) return <Page><PageHeader title="Conferência" back="/conferences" /><ErrorState message={error.message} onRetry={refetch} /></Page>;
  return <Editor key={`${data.id}-${data.status}`} conf={data} />;
}
